import { useState, useEffect, useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { filterTransactions, sortTransactionsByDate } from '../../../helpers/transactionHelpers';
import { GetPetDocumentsDateFilterSettings } from '../../../helpers/docHelpers';
import { getMinPolicyStartDate } from '../../../helpers/petHelpers';
import { useActivePet } from '../../../hooks/activePet';
import { TABLE_ROW_BATCH_SIZE } from '../../../constants/constants';
import { useLazyGetOutstandingPolicyPaymentQuery } from '../../../data/premiumsandpayments';
import { CSPUrlsMap } from '../../../routes/CSPUrlsMap';
import { setFullPolicyNumber } from '../../../store/slice/petDetailsSlice';
import { legacyPolicyNumberHandler } from '../../../helpers/paymentHelper';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { SLICES } from '../../../constants/slices';

export const usePaymentHistoryTable = ({ petDetails, transactionsData, selectedPolicyId, setSelectedPolicyId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isPayNowEnabled = cspConfiguration?.IsPayNowEnabled;
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;
  const { auth0AccessToken } = useSelector((state) => state?.[SLICES.AUTHENTICATION]?.authDetails || {});
  const [transactionsToShow, setTransactionsToShow] = useState(TABLE_ROW_BATCH_SIZE);
  const [outstandingPayments, setOutstandingPayments] = useState([]);

  const [getOutstandingPayment] = useLazyGetOutstandingPolicyPaymentQuery();
  const { today } = GetPetDocumentsDateFilterSettings();
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const minPolicyStartDate = getMinPolicyStartDate(petDetails);

  const { activePolicyId } = useActivePet({ petDetails });

  const initialFilterState = {
    policy: activePolicyId || selectedPolicyId,
    toDate: today,
    fromDate: minPolicyStartDate,
    type: ''
  };

  const [activeFilters, setActiveFilters] = useState(() => initialFilterState);
  const [filteredTransactions, setFilteredTransactions] = useState(
    sortTransactionsByDate({
      transactions: filterTransactions({
        transactions: transactionsData,
        filterSettings: initialFilterState
      })
    })
  );

  const [isDescendingSort, setIsDescendingSort] = useState(true);

  const handleSortOrderChange = () => {
    const newSortOrder = !isDescendingSort;
    setIsDescendingSort(newSortOrder);
    setFilteredTransactions(
      sortTransactionsByDate({
        transactions: filteredTransactions,
        descending: newSortOrder
      })
    );
  };

  const handleFilterChange = useCallback(
    ({ filterState }) => {
      const filtered = filterTransactions({
        transactions: transactionsData,
        filterSettings: filterState
      });

      setFilteredTransactions(
        sortTransactionsByDate({
          transactions: filtered,
          descending: isDescendingSort
        })
      );

      setSelectedPolicyId(filterState.policy);
      setActiveFilters(filterState);
    },
    [isDescendingSort, transactionsData, setSelectedPolicyId]
  );

  const goToPayNowPage = (fullPolicyNumber) => {
    navigate(CSPUrlsMap.payNow.url);
    dispatch(setFullPolicyNumber(fullPolicyNumber));
  };

  useEffect(() => {
    if (activeFilters.policy !== selectedPolicyId) {
      const updatedFilters = { ...activeFilters, policy: selectedPolicyId };
      setActiveFilters(updatedFilters);
      handleFilterChange({ filterState: updatedFilters });
    }
  }, [handleFilterChange, activeFilters, selectedPolicyId]);

  const handleShowMore = () => {
    setTransactionsToShow((prev) => prev + TABLE_ROW_BATCH_SIZE);
  };

  useEffect(() => {
    if (
      !isPayNowEnabled ||
      !petDetails?.length ||
      isV3PenTestRemediationEndpointsEnabled === undefined ||
      (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken)
    )
      return;

    petDetails.forEach(async (pet) => {
      try {
        const result = await getOutstandingPayment({
          partnerNumber: pet.PartnerNumber,
          policyNumber: legacyPolicyNumberHandler(
            isV3PenTestRemediationEndpointsEnabled ? pet.FullPolicyNumber : pet.PolicyNumber
          ),
          auth0Token: auth0AccessToken,
          isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
        }).unwrap();

        if (result.PremiumInstallment) {
          setOutstandingPayments((prev) => [...prev, ...result.PremiumInstallment]);
        }
      } catch (error) {
        throw Error('Error fetching outstanding payments:', error);
      }
    });
  }, [petDetails, getOutstandingPayment, auth0AccessToken, isV3PenTestRemediationEndpointsEnabled, isPayNowEnabled]);

  useEffect(() => {
    if (!outstandingPayments.length) return;

    const mappedOutstandingPayments = outstandingPayments.map((payment) => ({
      Date: payment.OriginalDueDate,
      Pet: payment.AnimalName,
      Amount: payment.DueAmount,
      FullPolicyNumber: payment.FullPolicyNumber,
      PolicyNumber: payment.PolicyNumber,
      Policy_InstallmentLineNumber: `${payment.PolicyNumber}_${payment.InstalmentLineNumber}`,
      Type: 'Premium',
      Status: 'Rejected'
    }));

    const combined = [...mappedOutstandingPayments, ...transactionsData];

    const filtered = filterTransactions({
      transactions: combined,
      filterSettings: activeFilters
    });

    const sorted = sortTransactionsByDate({
      transactions: filtered,
      descending: isDescendingSort
    });

    setFilteredTransactions(sorted);
  }, [outstandingPayments, transactionsData, activeFilters, isDescendingSort]);

  return {
    transactionsToShow,
    filteredTransactions,
    activeFilters,
    today,
    lastDayOfMonth,
    minPolicyStartDate,
    isDescendingSort,
    handleSortOrderChange,
    handleFilterChange,
    handleShowMore,
    goToPayNowPage
  };
};
